import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiPlus, FiTrendingUp, FiFilter, FiDownload, FiEye, FiEdit2, FiTrash2 } from 'react-icons/fi';
import { toast } from 'react-toastify';
import forecastService from '../../services/forecastService';
import './Forecasts.css';
import CommonModal from '../../services/CommonModal';
import { FaFileExcel } from 'react-icons/fa';

const Forecasts = () => {
  const navigate = useNavigate();
  const [forecasts, setForecasts] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    forecast_type: '',
    period_type: '',
    status: '',
    confidence_level: ''
  });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedForcastId, setSelectedForcastId] = useState(null);

  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 0
  });

  useEffect(() => {
    loadForecasts();
    loadStatistics();
  }, [filters, pagination.page]);

  const confirmDelete = () => {
    handleDelete(selectedForcastId);
    setShowDeleteModal(false);
    setSelectedForcastId(null);
  };


  const loadForecasts = async () => {
    try {
      setLoading(true);
      const params = {
        ...filters,
        page: pagination.page,
        limit: pagination.limit
      };

      // Remove empty filters
      Object.keys(params).forEach(key => {
        if (params[key] === '') delete params[key];
      });

      const response = await forecastService.getAll(params);
      setForecasts(response.data);
      setPagination(prev => ({
        ...prev,
        total: response.total,
        pages: response.pages
      }));
    } catch (error) {
      console.error('Error loading forecasts:', error);
      toast.error('Failed to load forecasts');
    } finally {
      setLoading(false);
    }
  };

  const loadStatistics = async () => {
    try {
      const response = await forecastService.getStatistics();
      setStatistics(response.data);
    } catch (error) {
      console.error('Error loading statistics:', error);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleClearFilters = () => {
    setFilters({
      forecast_type: '',
      period_type: '',
      status: '',
      confidence_level: ''
    });
  };

  const handleDelete = async (id) => {
      try {
        await forecastService.delete(id);
        toast.success('Forecast deleted successfully');
        loadForecasts();
        loadStatistics();
      } catch (error) {
        console.error('Error deleting forecast:', error);
        toast.error('Failed to delete forecast');
      }
    
  };

  const getStatusBadgeClass = (status) => {
    const statusClasses = {
      Draft: 'status-draft',
      Active: 'status-active',
      Completed: 'status-completed',
      Archived: 'status-archived'
    };
    return statusClasses[status] || 'status-default';
  };

  const getConfidenceBadgeClass = (confidence) => {
    const confidenceClasses = {
      Low: 'confidence-low',
      Medium: 'confidence-medium',
      High: 'confidence-high',
      'Very High': 'confidence-very-high'
    };
    return confidenceClasses[confidence] || 'confidence-default';
  };

  const formatCurrency = (value) => {
    if (!value) return '-';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'INR'
    }).format(value);
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const handleDownloadForecastList = () => {
  if (!forecasts || forecasts.length === 0) {
    toast.error('No forecasts to download');
    return;
  }

  const headers = [
    'Title', 'Project', 'Type', 'Period', 'Forecast Date',
    'Forecasted Value', 'Actual Value', 'Accuracy', 'Confidence', 'Status'
  ];

  const escapeCsv = (value) => {
    if (value === null || value === undefined) return '';
    const str = String(value);
    if (str.includes(',') || str.includes('"') || str.includes('\n')) {
      return `"${str.replace(/"/g, '""')}"`;
    }
    return str;
  };

  const rows = forecasts.map((forecast) => [
    forecast.title,
    forecast.project ? forecast.project.name : '',
    forecast.forecast_type,
    forecast.period_type,
    formatDate(forecast.forecast_date),
    forecast.forecasted_value ?? '',
    forecast.actual_value ?? '',
    forecast.accuracy ? `${forecast.accuracy}%` : '',
    forecast.confidence_level,
    forecast.status
  ]);

  const csvContent = [headers, ...rows]
    .map((row) => row.map(escapeCsv).join(','))
    .join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = url;
  link.download = `forecasts-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);

  toast.success('Forecast list downloaded');
};

  return (
    <div className="forecasts-page">
      <div className="forecasts-header">
        <div className="forecasts-header-left">
          <h1 className="forecasts-title">Forecasts</h1>
          <p className="forecasts-subtitle">Manage and track project forecasts</p>
        </div>
        <div className="forecasts-header-actions">
          <button
            className="btn-secondary"
            onClick={() => navigate('/app/forecasts/trends')}
          >
            <FiTrendingUp />
            View Trends
          </button>
          <button
            className="btn-primary"
            onClick={() => navigate('/app/forecasts/new')}
          >
            <FiPlus />
            New Forecast
          </button>
          <button
          className="px-4 py-3 bg-white border border-green-600 text-green-600 rounded-lg flex items-center gap-2"
          onClick={handleDownloadForecastList}
          title="Download forecast list"
        >
          <FaFileExcel />
        </button>
        </div>
      </div>

      {/* Statistics Cards */}
      {statistics && (
        <div className="forecasts-statistics">
          <div className="stat-card">
            <div className="stat-label">Total Forecasts</div>
            <div className="stat-value">{statistics.total}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Active</div>
            <div className="stat-value">{statistics.by_status.Active}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Completed</div>
            <div className="stat-value">{statistics.by_status.Completed}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Avg Accuracy</div>
            <div className="stat-value">{statistics.accuracy.average_accuracy}%</div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="forecasts-filters  filter-custom">
        <div className="filters-header">
          <FiFilter />
          <span>Filters</span>
        </div>
        <div className="filters-grid">
          <select
            name="forecast_type"
            value={filters.forecast_type}
            onChange={handleFilterChange}
            className="filter-select filter-custom"
          >
            <option value="">All Types</option>
            <option value="Budget">Budget</option>
            <option value="Timeline">Timeline</option>
            <option value="Resource">Resource</option>
            <option value="Revenue">Revenue</option>
            <option value="Risk">Risk</option>
            <option value="Quality">Quality</option>
          </select>

          <select
            name="period_type"
            value={filters.period_type}
            onChange={handleFilterChange}
            className="filter-select"
          >
            <option value="">All Periods</option>
            <option value="Weekly">Weekly</option>
            <option value="Monthly">Monthly</option>
            <option value="Quarterly">Quarterly</option>
            <option value="Yearly">Yearly</option>
          </select>

          <select
            name="status"
            value={filters.status}
            onChange={handleFilterChange}
            className="filter-select"
          >
            <option value="">All Statuses</option>
            <option value="Draft">Draft</option>
            <option value="Active">Active</option>
            <option value="Completed">Completed</option>
            <option value="Archived">Archived</option>
          </select>

          <select
            name="confidence_level"
            value={filters.confidence_level}
            onChange={handleFilterChange}
            className="filter-select"
          >
            <option value="">All Confidence Levels</option>
            <option value="Low">Low</option>
            <option value="Medium">Medium</option>
            <option value="High">High</option>
            <option value="Very High">Very High</option>
          </select>

          <button onClick={handleClearFilters} className="btn-clear-filters">
            Clear Filters
          </button>
        </div>
      </div>

      {/* Forecasts Table */}
      <div className="forecasts-table-container">
        {loading ? (
          <div className="forecasts-loading">Loading forecasts...</div>
        ) : forecasts.length === 0 ? (
          <div className="forecasts-empty">
            <p>No forecasts found</p>
            <button
              className="btn-primary"
              onClick={() => navigate('/app/forecasts/new')}
            >
              Create First Forecast
            </button>
          </div>
        ) : (
          <>
            <table className="forecasts-table">
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Type</th>
                  <th>Period</th>
                  <th>Forecast Date</th>
                  <th>Forecasted Value</th>
                  <th>Actual Value</th>
                  <th>Accuracy</th>
                  <th>Confidence</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {forecasts.map((forecast) => (
                  <tr key={forecast.id}>
                    <td>
                      <div className="forecast-title-cell">
                        <span className="forecast-title">{forecast.title}</span>
                        {forecast.project && (
                          <span className="forecast-project">{forecast.project.name}</span>
                        )}
                      </div>
                    </td>
                    <td>
                      <span className="forecast-type-badge">{forecast.forecast_type}</span>
                    </td>
                    <td>{forecast.period_type}</td>
                    <td>{formatDate(forecast.forecast_date)}</td>
                    <td>{formatCurrency(forecast.forecasted_value)}</td>
                    <td>{formatCurrency(forecast.actual_value)}</td>
                    <td>
                      {forecast.accuracy ? (
                        <span className={`accuracy-badge ${forecast.accuracy >= 80 ? 'accuracy-high' : forecast.accuracy >= 60 ? 'accuracy-medium' : 'accuracy-low'}`}>
                          {forecast.accuracy}%
                        </span>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td>
                      <span className={`confidence-badge ${getConfidenceBadgeClass(forecast.confidence_level)}`}>
                        {forecast.confidence_level}
                      </span>
                    </td>
                    <td>
                      <span className={`status-badge ${getStatusBadgeClass(forecast.status)}`}>
                        {forecast.status}
                      </span>
                    </td>
                    <td>
                      <div className="forecast-actions">
                        <button
                          className="text-blue-600 hover:text-blue-900"
                          onClick={() => navigate(`/app/forecasts/${forecast.id}`)}
                          title="View"
                        >
                          <FiEye />
                        </button>


                        <button
                          className="text-blue-600 hover:text-blue-900"
                          onClick={() => navigate(`/app/forecasts/${forecast.id}/edit`)}
                          title="Edit"
                        >
                          <FiEdit2 />
                        </button>
                        <button
                          className="text-red-600 hover:text-red-900"
                          onClick={() => {
                            setSelectedForcastId(forecast.id);
                            setShowDeleteModal(true);
                          }}

                          title="Delete"
                        >
                          <FiTrash2 />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Pagination */}
            {pagination.pages > 1 && (
              <div className="forecasts-pagination">
                <button
                  className="pagination-btn"
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                  disabled={pagination.page === 1}
                >
                  Previous
                </button>
                <span className="pagination-info">
                  Page {pagination.page} of {pagination.pages}
                </span>
                <button
                  className="pagination-btn"
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                  disabled={pagination.page === pagination.pages}
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>
      <CommonModal
        isOpen={showDeleteModal}
        title="Delete Vendor"
        message="Are you sure you want to delete this forecast?"
        onClose={() => {
          setShowDeleteModal(false);
          setSelectedForcastId(null);
        }}
        onConfirm={confirmDelete}
        type="error"
        confirmText="Delete"
        cancelText="Cancel"
      />
    </div>
  );
};

export default Forecasts;

